/** QA verdict: one button per category (keys 1–9, 0, -), a note, Save (Enter) with If-Match. 409 shows the conflict
 * and a Reload; 422 lists every detail. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ifMatch, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { errorText, WriteError } from "../common";
import { KeyButton, useKeyBindings, type KeyBinding } from "../keys";
import { QA_CATEGORIES, qaLabel, type QaCategory, type RenderDetail } from "../types";

export function QaForm({ render }: { render: Pick<RenderDetail, "id" | "version" | "qa_category" | "qa_actor"> }) {
  const qc = useQueryClient();
  const announce = useAnnounce();
  const [cat, setCat] = useState<QaCategory | null>(null);
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: (v: { category: QaCategory; note: string; version: number }) =>
      unwrap(api.POST("/api/renders/{render_id}/qa", { params: { path: { render_id: render.id }, header: ifMatch(v.version) },
        body: { category: v.category, note: v.note.trim() || null } })),
    onSuccess: (r) => {
      announce(`QA saved for render #${r.id}: ${qaLabel(r.qa_category)}${r.approved ? " (approved)" : ""}`);
      setCat(null);
      setNote("");
      void qc.invalidateQueries({ queryKey: qk.entity("render", render.id) });
    },
    onError: (e) => announce(`QA save failed: ${errorText(e)}`, "error"),
  });
  const submit = () => {
    if (!cat) {
      announce("Pick a QA category first", "error");
      return;
    }
    if (!save.isPending) save.mutate({ category: cat, note, version: render.version });
  };
  const bindings: KeyBinding[] = QA_CATEGORIES.map((q) => ({ key: q.key, label: q.label, testId: `qa-${q.id}`,
    run: () => setCat(q.id) }));
  const saveKey: KeyBinding = { key: "Enter", label: "Save verdict", testId: "qa-save", run: submit,
    disabled: !cat || save.isPending };
  useKeyBindings([...bindings, saveKey]);
  return (
    <section className="ws8-panel" aria-labelledby="ws8-qa-h" data-testid="qa-form">
      <h2 id="ws8-qa-h" className="ws8-h2">QA verdict</h2>
      <p className="ws8-muted" data-testid="qa-current">
        Current: {qaLabel(render.qa_category)}{render.qa_actor ? ` by ${render.qa_actor}` : ""} · version {render.version}
      </p>
      <div className="ws8-qa" role="group" aria-label="QA category">
        {QA_CATEGORIES.map((q, i) => <KeyButton key={q.id} b={bindings[i] as KeyBinding} pressed={cat === q.id} />)}
      </div>
      <label htmlFor="ws8-qa-note">Note</label>
      <textarea id="ws8-qa-note" data-testid="qa-note" maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="ws8-row">
        <KeyButton b={saveKey} className="ws8-primary" />
        <span className="ws8-muted" data-testid="qa-selected">Selected: {cat ? qaLabel(cat) : "none"}</span>
      </div>
      <WriteError error={save.error} testId="qa-conflict" onReload={() => {
        save.reset();
        void qc.invalidateQueries({ queryKey: qk.entity("render", render.id) });
      }} />
    </section>
  );
}
